<template>
  <div ref="outlineTree" @click="outlineClick">
    <DraggableTree
      id="outline-tree"
      class="outline-tree uk-position-relative"
      :class="{
        'input-mode': enableInput,
        compressed: isCompressed,
      }"
      :data="outlineNodes"
      :draggable="draggable && permissionLevel !== PermissionLevel.COHORT"
      :indent="36"
      :getTriggerEl="customDragElement"
      :ondragend="handleDragEnd"
    >
      <div
        slot-scope="{ data, store }"
        class="outline-node uk-animation-slide-top-small uk-animation-fast"
        :class="{
          active: data._id === hoverNodeId || data._id === focusNodeId,
        }"
        :style="{
          ...outlineNodeStyle(data),
        }"
        :data-node-id="data._id"
        :data-node-key="data.nodeKey"
        @click="focusNode(data)"
      >
        <template v-if="!data.isDragPlaceHolder">
          <div
            :id="`outline-node-caret-${data._id}`"
            :ref="`outline-node-caret-${data._id}`"
            class="outline-node-caret"
            :class="{
              'outline-node-caret-open arrowOpen': data.children?.length && data.open,
              'outline-node-caret-parent arrowClose': data.children?.length && !data.open,
              'drag-icon': draggable,
              'uk-drag': draggable,
            }"
            @click="toggleOutlineNodeClicked(data, store)"
          >
            <img
              class="outline-node-caret-img arrow-img"
              :src="
                data.children?.length
                  ? `/icons/black/icon-black-caret-rounded.svg`
                  : `/icons/black/icon-black-caret-hollow-rounded.svg`
              "
            />
          </div>
          <span
            class="outline-node-content uk-display-inline-block th-padding-xxs uk-margin-small-left uk-width-1-1"
            :class="{
              [`nodeOrder-${data.nodeOrder}`]: true,
            }"
            @click="handleOutlineClick(data)"
          >
            <div
              class="outline-node-line outline-node-line-parent"
              v-if="data.open && data.children && data.children.length"
              :style="{
                height: data.parentNodeLineHeight,
              }"
            ></div>
            <div class="outline-node-line outline-node-line-child" v-if="data.level > 1"></div>
            <div
              class="outline-node-line outline-node-line-parent-root"
              v-if="data.level <= 1"
              :style="{
                height: data.nodeHeight,
              }"
            ></div>
            <OutlineNode
              :id="`outline-node-${data._id}`"
              :ref="`outline-node-${data._id}`"
              :class="`outline-node-key-${data.nodeKey}`"
              :data-node-order="data.nodeOrder"
              :nodeData="data"
              :canAddCitations="enableInput && roundStatus === RoundStatus.EDITING_BASIS"
              @hoverIn="outlineNodeHoverIn"
              @hoverOut="outlineNodeHoverOut"
              @updated="outlineNodeUpdated(data)"
            >
              <template v-if="!enableInput && optionTrue">
                <div class="paragraphs">
                  <AnnotatedParagraph
                    class="outline-node-content-text"
                    :removeMargin="true"
                    :paragraph="
                      isFullView ? outlineParagraphsFull[data._id] : outlineParagraphs[data._id]
                    "
                    :node-annotations="allNodeAnnotations"
                    element-id-prefix="outline"
                    :geometry-terminal-element-id="'outlineVirtualScrollContainer'"
                    @mouseEnterNodeOrSpan="doMouseEnter"
                    @geometriesUpdated="updateParagraphGeometries"
                    @mouseClickNode="doSingleClick(data, $event)"
                    @dblclick="doDoubleClick"
                  />
                </div>
                <div class="arrows" v-if="mainPanelView === 'outline' && !fullTextView">
                  <template v-if="counterpointLoaded">
                    <AnnotationArrow
                      v-for="ann in outlineAnnotations[data._id]"
                      :key="`arr${ann._id}`"
                      :annotation="ann"
                      arrow-type="text"
                      :outlineId="data._id"
                      :ref="`annotation-arrow-${ann._id}`"
                      type="text"
                    />
                  </template>
                </div>
              </template>

              <div
                v-else
                class="outline-node-input"
                :class="{
                  'has-focus': data.hasFocus,
                  'text-selected': isTextSelected(data),
                  'outline-node-content-text-nlp':
                    roundStatus === RoundStatus.EDITING_NLP_OUTLINE ||
                    roundStatus === RoundStatus.EDITING_OUTLINED_ANALYZED,
                  'outline-node-content-text': roundStatus === RoundStatus.EDITING_BASIS,
                }"
                @mouseenter="doMouseEnter(data)"
                @keydown.tab.prevent="doTab($event, data)"
              >
                <TipTap
                  :ref="`outline-node-input-${data._id}`"
                  :id="`outline-node-input-${data._id}`"
                  v-model="data.nodeContent"
                  @updateModelValue="updateModel(data)"
                  :max-characters="200"
                  @keydown="doOutlineInputKeyDown($event, data)"
                  @focusIn="doOutlineNodeFocusIn(data)"
                  @focusOut="doOutlineNodeFocusOut(data)"
                >
                </TipTap>
              </div>
            </OutlineNode>
          </span>
          <span
            v-if="
              draggable &&
              (data.parent?.nodeKey ||
                data.children?.length ||
                data.parent?.children?.length > 1) &&
              permissionLevel !== PermissionLevel.COHORT
            "
            class="outline-node-remove-button"
            :class="{ 'outline-ban-icon': roundStatus === RoundStatus.EDITING_NLP_OUTLINE }"
          >
            <span uk-icon="ban" class="ban-icon" @click="handleDeleteNode(data, store)"></span>
          </span>
        </template>
      </div>
    </DraggableTree>

    <div v-if="deleteNode" class="undo-passage">
      <div style="width: 5px; background: #d19053"></div>
      <div style="background: #fff6ef; padding: 0 5px; display: flex; align-items: center">
        <div uk-icon="icon:warning;" style="color: #e49141" />
        <span style="margin: 0 5px">You have deleted a passage from the outline</span>
        <button
          @click="handleNodeUndo"
          class="uk-button uk-button-secondary"
          style="border-radius: 25px"
        >
          UNDO
        </button>
      </div>
    </div>
    <div
      v-if="deleteNode && selectNode"
      class="select-node"
      :class="{ 'select-node-left': mainPanelView === 'outline' }"
    >
      <div style="width: 3px; background: #254858"></div>
      <div style="background: #deecf4; padding: 0 5px">Please Select a node</div>
    </div>
  </div>
</template>

<script>
import LoadingStatus from '@/assets/js/enum/LoadingStatus';
import { DraggableTree } from 'vue-draggable-nested-tree';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import {
  arrayEquals,
  getElementAbsolutePosition,
  getOutlineNodeForDisplay,
  forEachOutlineNode,
  getOutlineNodeForTransfer,
} from '@/assets/js/utils';
import { excerptMaxChars, ellipsisText } from '@/assets/js/constants';
import { EventBus } from '@/assets/js/event-bus';
import AnnotatedParagraph from '@/components/general/AnnotatedParagraph';
import AnnotationArrow from '@/components/annotations/AnnotationArrow';
import OutlineNode from '@/components/outline/OutlineNode';
import TipTap from '@/components/annotations/TipTap';
import patienceDiff from '@/assets/js/PatienceDiff.js';
import RoundStatus from '@/assets/js/enum/RoundStatus';
import PermissionLevel from '@/assets/js/enum/PermissionLevel';

export default {
  name: 'OutlineTreeInput',

  components: {
    DraggableTree,
    OutlineNode,
    AnnotatedParagraph,
    AnnotationArrow,
    TipTap,
  },

  props: {
    outlineSource: Object,
    virtualScrollContainer: Object,

    draggable: {
      type: Boolean,
      default: false,
    },

    deleteNode: {
      type: Boolean,
      default: false,
    },
    isFullView: {
      type: Boolean,
      default: false,
    },
    enableInput: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      geometries: {},
      sourceElementContainerBoundary: 0,
      lastOutlineNodeKeyNumber: 1,
      lastUpdatedNodeId: '',
      lastUpdatedNodeOrder: '',
      flatOutlineBeforeEdits: [],
      outlineNodes: [],
      nodeCount: 0,
      outlineParagraphs: {},
      outlineParagraphsFull: {},
      ellipsisAnnotations: {},
      hoverNodeId: '',
      focusNodeId: '',
      isOutlineUpdated: 0,
      isNodeUndo: false,
      isNodeDragged: false,
      addOrDeleteNode: false,
      selectNode: false,
      addNodeOpen: '',
      activeAnnotation: false,
      deletedNode: [],
    };
  },

  emits: ['textClick'],

  computed: {
    EventBus() {
      return EventBus;
    },
    patienceDiff() {
      return patienceDiff;
    },
    RoundStatus() {
      return RoundStatus;
    },
    PermissionLevel() {
      return PermissionLevel;
    },
    ...mapState([
      'selectedItem',
      'counterpointLoaded',
      'counterpointGeometries',
      'counterpointOffsets',
      'defaultCharacterLimit',
      'mainPanelView',
      'showingCounterpointSidePanel',
      'nodeId',
      'fullTextView',
      'counterpointData',
      'newAnnotationPosition',
      'articleParagraphs',
      'basisEdited',
      'documentData',
      'optionTrue',
    ]),

    ...mapGetters([
      'canEditOutline',
      'hoverOutlineNodesIds',
      'documentNodeAnnotations',
      'documentNodeLLMAnnotations',
      'flatOutlineTextNodes',
      'outlineAnnotations',
      'getSingleNodeSpan',
      'useExcerpts',
      'outlineNodeStyle',
      'myLatestRoundNumber',
      'flatOutlineNodes',
      'roundStatus',
      'permissionLevel',
    ]),

    isCompressed() {
      return this.counterpointData?.rounds?.length > 3;
    },

    flatOutlineSource() {
      return this.flatOutlineNodes(
        this.outlineSource.outline.outline
          ? this.outlineSource.outline.outline
          : this.outlineSource.outline
      );
    },

    allNodeAnnotations() {
      return {
        ...this.documentNodeAnnotations,
        ...this.documentNodeLLMAnnotations,
        ...this.ellipsisAnnotations,
      };
    },
  },

  mounted() {
    if (this.outlineSource) {
      this.setupOutline();
      this.setupOutlineHumanOutlineKey();
    }
    this.setNodeId(this.nodeId);
    this.setSourceElementContainerBoundary();
    this.$nextTick(() => {
      this.emitOutlineToggled();
    });
    EventBus.on('workflow.drafting.finalized', this.setupOutline);
    EventBus.on('outline.collapseAll', this.collapseAll);
    EventBus.on('outline.expandAll', this.expandAll);
    EventBus.on('addnode.option', this.handleNodeAdd);
    EventBus.on('annotation.active', this.handleAnnotationActive);
    EventBus.on('save.edited.text', this.saveOutline);
    EventBus.on('outline.content.save', this.saveOutline);
  },

  beforeUnmount() {
    EventBus.off('workflow.drafting.finalized', this.setupOutline);
    EventBus.off('outline.collapseAll', this.collapseAll);
    EventBus.off('outline.expandAll', this.expandAll);
    EventBus.off('addnode.option', this.handleNodeAdd);
    EventBus.off('annotation.active', this.handleAnnotationActive);
    EventBus.off('save.edited.text', this.saveOutline);
    EventBus.off('outline.content.save', this.saveOutline);
  },

  watch: {
    outlineSource(value) {
      this.setupOutlineNodes(value);

      if (!this.nodeId || !Object.keys(this.nodeId).length) {
        this.initExpandedOutlineNodesIds();
      }
      this.setupOutlineHumanOutlineKey();
    },

    showingCounterpointSidePanel() {
      setTimeout(() => {
        this.$forceUpdate();
      }, 500);
    },

    outlineNodes(value) {
      if (value.length === 1) {
        this.$refs[`outline-node-input-${value[0]._id}`]?.setFocus();
      }
      if (!this.enableInput) {
        this.$emit('updated', value);
      }
    },

    allNodeAnnotations: {
      handler() {
        this.setupOutlineParagraphs();

        // Emit event after DOM update to ensure arrows can find their targets
        this.$nextTick(() => {
          EventBus.emit('outline.paragraphs.updated');
        });
      },
      deep: true,
    },

    isOutlineUpdated() {
      // focus updated node
      setTimeout(() => {
        if (this.lastUpdatedNodeId) {
          this.$refs[`outline-node-input-${this.lastUpdatedNodeId}`].setFocus();
          this.lastUpdatedNodeId = null;
        } else if (this.lastUpdatedNodeOrder) {
          document
            .querySelector(`[data-node-order="${this.lastUpdatedNodeOrder}"] .ProseMirror`)
            ?.focus();
          this.lastUpdatedNodeOrder = null;
        }
      }, 100);
    },
  },

  methods: {
    ...mapMutations(['setNodeId', 'setBasisEdited', 'setNewAnnotationPosition']),

    ...mapActions([
      'setSelectedOutlineNode',
      'updateOutline',
      'showToastAlert',
      'getNodeFromText',
      'getDocumentWithCounterpoints',
      'resetMenu',
      'updateDocumentSource',
      'updateSourceDocument',
    ]),

    handleAnnotationActive(id) {
      if (id) {
        let elm = document.getElementsByClassName(`counterpoint-ref-${id}`)[0];
        this.hoverNodeId = elm.id.split('_')[2];
        this.activeAnnotation = true;
      } else {
        this.activeAnnotation = false;
        this.hoverNodeId = '';
      }
      this.setupOutlineParagraphs();
    },

    customDragElement(nodeVm) {
      return nodeVm.$el?.querySelector('.drag-icon');
    },
    /**
     * Returns an array with the outline node an its children flattened
     */
    getFlatOutlineNodes() {
      return this.flattenOutline(this.outlineNodes);
    },
    // ***
    updateModel(data) {
      if (data.textFragmentContent) {
        data.textFragmentContent = data.textFragmentContent.replace(/<p><\/p>/gi, '');
      } else {
        data.nodeContent = data.nodeContent.replace(/<p><\/p>/gi, '');
      }
    },
    handleOutlineClick(data) {
      if (this.selectNode) {
        let elms = document.getElementsByClassName('text-selected');
        for (let i = 0; i < elms.length; i++) {
          if (elms[i].id.includes('article')) {
            let id = elms[i].id.split('_');
            let nodeData = this.articleParagraphs[id[2]].spans[id[4]];
            if (this.addNodeOpen === 'addnode') {
              data.nodeContent += ' ' + nodeData.fullText;
              data.nodes = [...data.nodes, ...nodeData.nodes];
            } else if (this.addNodeOpen === 'addbelownode') {
              let node = { ...data };
              node.nodeContent = nodeData.fullText;
              node.nodes = nodeData.nodes;
              node.paragraphId = id[2];
              node.nodeKey = `Summary_${id[2]}`;
              node.nodeOrder = `Summary_${id[2]}`;
              node.children = [];
              node.sublevel = [];
              node.basisNodes = nodeData.nodes;
              delete node._id;
              if (this.addNodeOpen === 'addbelownode') {
                data.parent.children.splice(data.index + 1, 0, node);
              }
              let updatedNodesData = [...this.outlineNodes];
              updatedNodesData = updatedNodesData.map(item => {
                if (item._id === nodeData._id) {
                  item.sublevel = item.children;
                }
                return item;
              });
              this.outlineNodes = updatedNodesData;
            }
          }
        }
        this.addOrDeleteNode = true;
        this.selectNode = false;
        this.resetMenu(true);
      }
    },

    handleNodeAdd(data) {
      this.addNodeOpen = data;
      this.selectNode = true;
      setTimeout(() => {
        this.selectNode = false;
        this.resetMenu(true);
      }, 5000);
    },

    isTextSelected(span) {
      if (!span?.basisNodes) {
        return false;
      }
      if (span.basisNodes) {
        let selectedData =
          span?.basisNodes?.filter(
            data =>
              parseInt(data.nodeKey.split('-')[0].slice(1)) >=
                parseInt(this.newAnnotationPosition?.from?.nodeKey.split('-')[0].slice(1)) &&
              parseInt(data.nodeKey.split('-')[1]) <=
                parseInt(this.newAnnotationPosition?.to?.nodeKey.split('-')[1])
          ).length > 0;
        if (selectedData) {
          let id = span.basisNodes[0]?.paragraphId;
          let articleElem = document.getElementById(`article_P_${id}`);
          articleElem.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
        return selectedData;
      }
    },
    setupOutlineHumanOutlineKey() {
      if (this.roundStatus !== RoundStatus.EDITING_NLP_OUTLINE) {
        this.lastOutlineNodeKeyNumber =
          this.getFlatOutlineNodes()
            ?.map(node => parseInt(node.nodeKey.split('_')[1]))
            ?.sort((n1, n2) => n2 - n1)[0] + 1;
      }
    },

    handleDeleteNode(node, store) {
      let index = node.parent.children.findIndex(x => x._id === node._id);
      node.children = node.children.map(child => {
        child.level = node.level;
        child.parent = node.parent;
        return child;
      });
      node.parent.children = [
        ...node.parent.children.slice(0, index),
        ...node.children,
        ...node.parent.children.slice(index),
      ];
      this.deletedNodes = [...this.outlineNodes];
      store.deleteNode(node);
      if (this.deleteNode) {
        document.getElementsByClassName('undo-passage')[0].style.display = 'flex';
      }
      setTimeout(() => {
        if (!this.isNodeUndo) {
          this.addOrDeleteNode = true;
          if (this.deleteNode) {
            document.getElementsByClassName('undo-passage')[0].style.display = 'none';
          }
          let updatedNodesData = [...this.outlineNodes];
          if (node.parent.isRoot) {
            updatedNodesData = node.parent.children;
          } else {
            let nodeData = { ...node };
            while (!nodeData.parent.isRoot) {
              nodeData = nodeData.parent;
            }
            if (nodeData._id) {
              updatedNodesData = updatedNodesData.map(data => {
                if (data._id === nodeData._id) {
                  data.sublevel = data.children;
                }
                return data;
              });
            }
          }
          this.outlineNodes = updatedNodesData;
          this.setBasisEdited(true);
        }
      }, 3000);
    },

    handleNodeUndo() {
      this.isNodeUndo = true;
      this.outlineNodes = this.deletedNodes;
      this.deletedNodes = [];
      document.getElementsByClassName('undo-passage')[0].style.display = 'none';
      this.$emit('updated');
    },

    getLastNode(spans) {
      const lastSpan = spans[spans.length - 1];
      const lastNode = lastSpan.nodes[lastSpan.nodes.length - 1];
      return lastNode;
    },

    doSingleClick(data, event) {
      this.doMarkupClick(event);
      if (this.useExcerpts) {
        const parentBox = this.$refs[`outline-node-${data._id}`];
        EventBus.emit('outline.node.click', data, parentBox);
      }
    },

    doDoubleClick(outlineId) {
      if (this.myLatestRoundNumber > 3) {
        this.$emit('dblclick', outlineId);
      }
    },

    /**
     * Parse nodes for display in the Vue-Draggable-Tree
     */
    // ***
    setupOutlineNodes(value) {
      let outlineVal = value?.outline?.outline ? value?.outline?.outline : value?.outline;
      if (outlineVal) {
        this.outlineNodes = outlineVal.map((node, index) => getOutlineNodeForDisplay(node, index));
        this.updateFlatOutlineBeforeEdits(outlineVal.map(node => getOutlineNodeForDisplay(node)));
      }
    },

    updateFlatOutlineBeforeEdits(outline) {
      this.flatOutlineBeforeEdits = this.flattenOutline(outline);
    },

    /**
     * Sets the "outlineParagraphs" object with outline node ids as properties mapped to the paragraph information required by the AnnotatedParagraph component,
     * Gets text excerpts when the "useExcerpts" property returns true
     * and sets the "ellipsisAnnotations" object with the hidden annotations
     */
    setupOutlineParagraphs() {
      if (this.enableInput) {
        return;
      }

      const paragraphs = {};
      const paragraphsFull = {};
      for (const outlineNodeId in this.flatOutlineTextNodes) {
        const spans = [];
        let span = {};

        //
        // add annotation references to paragraph spans
        const textNodes = this.flatOutlineTextNodes[outlineNodeId] || [];
        for (const node of textNodes) {
          let nodeAnnotations = {};
          if (node.source === 'groundedTextNodes') {
            nodeAnnotations = this.documentNodeLLMAnnotations;
          } else {
            nodeAnnotations = this.documentNodeAnnotations;
          }
          const nodeSentenceId = node.sentenceId || 1;
          if (nodeAnnotations[node.nodeKey]) {
            if (!span.counterpointAnnotationIDs && nodeAnnotations[node.nodeKey]) {
              span.counterpointAnnotationIDs =
                nodeAnnotations[node.nodeKey].counterpointAnnotationIDs;
              span.secondPart = this.flatOutlineTextNodes[outlineNodeId].map(x => x.index)[0];
              span.sentenceId = nodeSentenceId;
            }

            if (
              span.sentenceId != nodeSentenceId ||
              !arrayEquals(
                span.counterpointAnnotationIDs,
                nodeAnnotations[node.nodeKey].counterpointAnnotationIDs
              )
            ) {
              span.fullText = span.nodes.map(x => x.nodeContent).join('');
              spans.push(span);
              span = {};
              span.secondPart = this.flatOutlineTextNodes[outlineNodeId].map(x => x.index)[0];
              span.counterpointAnnotationIDs =
                nodeAnnotations[node.nodeKey].counterpointAnnotationIDs;
              span.sentenceId = nodeSentenceId;
            }
            span.nodes = this.canEditOutline
              ? this.flatOutlineTextNodes[outlineNodeId]
              : [...(span.nodes || []), node];
          }
        }
        if (span.nodes) {
          span.fullText = this.canEditOutline
            ? this.flatOutlineTextNodes[outlineNodeId].map(x => x.nodeContent).join('')
            : span.nodes.map(x => x.nodeContent).join('');
          spans.push(span);
        }
        if (!span.fullText && textNodes.length) {
          span.counterpointAnnotationIDs = [];
          span.nodes = textNodes;
          span.fullText = textNodes.map(x => x.nodeContent).join('');
          spans.push(span);
        }
        //
        // set spanOrder
        spans.forEach((span, i) => (span.spanOrder = i));

        const allAnnotationIds = new Set();
        for (const span of spans) {
          for (const annotationId of span.counterpointAnnotationIDs) {
            allAnnotationIds.add(annotationId);
          }
        }
        const finalSpans =
          this.useExcerpts && this.hoverNodeId !== outlineNodeId
            ? this.getNodeExcerpt(outlineNodeId, spans)
            : spans;

        //
        // set spans to paragraphs by outline node
        paragraphs[outlineNodeId] = {
          paragraphId: outlineNodeId,
          nodes: this.flatOutlineTextNodes[outlineNodeId],
          fullText: this.flatOutlineTextNodes[outlineNodeId]
            ?.map(node => node.nodeContent)
            ?.join(''),
          spans: finalSpans,
          counterpointAnnotationIDs: Array.from(allAnnotationIds),
        };
        paragraphsFull[outlineNodeId] = {
          paragraphId: outlineNodeId,
          nodes: this.flatOutlineTextNodes[outlineNodeId],
          fullText: this.flatOutlineTextNodes[outlineNodeId]
            ?.map(node => node.nodeContent)
            ?.join(''),
          spans: spans,
          counterpointAnnotationIDs: Array.from(allAnnotationIds),
        };
      } // END FOR
      this.outlineParagraphs = paragraphs;
      this.outlineParagraphsFull = paragraphsFull;
    },

    /**
     * Create text excerpts
     */
    getNodeExcerpt(outlineNodeId, spans) {
      //
      // get excerpt from first annotated text within the node
      const firstAnnotatedSpan = spans.find(span => span.counterpointAnnotationIDs.length);
      let excerpt = firstAnnotatedSpan ? [firstAnnotatedSpan] : [];

      //
      // if an annotation was found...
      if (excerpt.length) {
        const firstChar = excerpt[0].nodes[0].offsets.start_char;

        if (excerpt[0].fullText.length >= excerptMaxChars) {
          //
          // filter excerpt nodes to maximum excerpt characters
          excerpt[0].nodes = excerpt[0].nodes.filter(
            n => n.offsets.end_char - firstChar < excerptMaxChars
          );

          excerpt[0].fullText = excerpt[0].nodes.map(n => n.nodeContent).join('');

          this.appendEmptyEllipsis(excerpt, outlineNodeId);

          // if not first span, prepend ellipsis
          const oldFirstChar = spans[0].nodes[0].offsets.start_char;
          if (firstChar > oldFirstChar) {
            const startEllipsisEndChar = firstChar;
            const startEllipsisStartChar = startEllipsisEndChar - ellipsisText.length;

            let startEllipsisSpan = this.getEllispsisSpan(
              outlineNodeId,
              excerpt[0].nodes[0].nodeOrder - 1,
              startEllipsisStartChar,
              startEllipsisEndChar
            );

            excerpt = [startEllipsisSpan, ...excerpt];
          }
        } else {
          let excerptStart = excerpt[0].nodes[0].offsets.start_char;
          let excerptEnd = excerpt[0].nodes[excerpt[0].nodes.length - 1].offsets.end_char;
          let excerptLength = excerptEnd - excerptStart;
          //
          // annotated text is not the first span; has spans before
          let charactersLeft = excerptMaxChars - excerptLength;

          let previousSpans = [];
          if (excerpt[0].spanOrder > 0) {
            //
            // get spans before annotated text in excerpt
            const halfCharsLeft = charactersLeft / 2;
            const minExcerptStart = excerptStart - halfCharsLeft;
            previousSpans = this.getExcerptSpans({
              spans,
              limitFn: (charactersLeft, span) =>
                span.spanOrder < excerpt[0].spanOrder && charactersLeft > 0,
              filterFn: n => n.offsets.start_char > minExcerptStart,
              charactersLeft: halfCharsLeft,
            });
            //
            // update total characters left
            charactersLeft = halfCharsLeft;
          }

          //
          // annotated text is not the last span; has spans after
          let nextSpans = [];
          if (excerpt[0].spanOrder < spans.length) {
            const maxExcerptEnd = excerptEnd + charactersLeft;

            nextSpans = this.getExcerptSpans({
              spans,
              limitFn: (charactersLeft, span) =>
                span.spanOrder > excerpt[0].spanOrder && charactersLeft > 0,
              filterFn: n => n.offsets.start_char < maxExcerptEnd,
              charactersLeft,
            });

            if (charactersLeft > 0) {
              const minExcerptStart =
                excerptStart - charactersLeft - previousSpans.map(s => s.fullText).join('').length;

              previousSpans = this.getExcerptSpans({
                spans,
                limitFn: (charactersLeft, span) =>
                  span.spanOrder < excerpt[0].spanOrder && charactersLeft > 0,
                filterFn: n => n.offsets.start_char > minExcerptStart,
                charactersLeft,
              });
            }
          }

          //
          // add ellipsis to previous spans
          if (
            previousSpans &&
            previousSpans.length &&
            previousSpans[0].nodes &&
            previousSpans[0].nodes.length
          ) {
            const firstNode = previousSpans[0].nodes[0];
            const previousEllipsisEndChar = firstNode.offsets.start_char;
            const previousEllipsisStartChar = previousEllipsisEndChar - ellipsisText.length;

            const oldStartChar = spans[0].nodes[0].offsets.start_char;

            if (previousEllipsisEndChar > oldStartChar) {
              let previousEllipsisSpan = this.getEllispsisSpan(
                outlineNodeId,
                firstNode.nodeOrder - 1,
                previousEllipsisStartChar,
                previousEllipsisEndChar
              );

              previousSpans = [previousEllipsisSpan, ...previousSpans];
            }
          }

          //
          // add ellipsis to next spans
          if (nextSpans && nextSpans.length) {
            const lastNode = this.getLastNode(nextSpans);
            const endEllipsisStartChar = lastNode.offsets.end_char;
            const endEllipsisEndChar = endEllipsisStartChar + ellipsisText.length;

            const oldLastNode = this.getLastNode(spans);
            const oldEndEllipsisEndChar = oldLastNode.offsets.end_char;

            if (endEllipsisStartChar < oldEndEllipsisEndChar) {
              const hiddenSpansAnnotationIds = spans
                .filter(s => s.spanOrder > nextSpans[nextSpans.length - 1].spanOrder)
                .flatMap(s => s.counterpointAnnotationIDs);

              this.ellipsisAnnotations = {
                ...this.ellipsisAnnotations,
                [`Node_${outlineNodeId}_${endEllipsisStartChar}-${endEllipsisEndChar}`]: {
                  counterpointAnnotationIDs: hiddenSpansAnnotationIds,
                },
              };

              let endEllipsisSpan = this.getEllispsisSpan(
                outlineNodeId,
                lastNode.nodeOrder + 1,
                endEllipsisStartChar,
                endEllipsisEndChar,
                hiddenSpansAnnotationIds
              );

              nextSpans = [...nextSpans, endEllipsisSpan];
            }
          }

          //
          // add all spans to excerpt
          excerpt = [...previousSpans, ...excerpt, ...nextSpans];
        }
      }

      //
      // if no annotation to excerpt was found
      // get the excerpt from the full outline node text
      else if (spans.length && spans[0].nodes.length) {
        const firstChar = spans[0].nodes[0].offsets.start_char;
        let charactersLeft = excerptMaxChars;

        const excerptSpans = this.getExcerptSpans({
          spans,
          limitFn: charsLeft => charsLeft > 0,
          filterFn: n => n.offsets.end_char - firstChar < charactersLeft,
          charactersLeft,
        });

        this.appendEmptyEllipsis(excerptSpans, outlineNodeId);

        excerpt = excerptSpans;
      }

      return excerpt;
    },

    getEllispsisSpan(nodeId, nodeOrder, start_char, end_char, annotationIds) {
      return this.getSingleNodeSpan(
        ellipsisText,
        `Node_${nodeId}_`,
        nodeOrder,
        'Token',
        start_char,
        end_char,
        1,
        1,
        annotationIds || []
      );
    },

    appendEmptyEllipsis(spans, outlineNodeId) {
      const lastNode = this.getLastNode(spans);
      const ellipsisStartChar = lastNode?.offsets?.end_char || 0;
      const ellipsisEndChar = ellipsisStartChar + ellipsisText.length;

      let ellipsisSpan = this.getEllispsisSpan(
        outlineNodeId,
        (lastNode?.nodeOrder || 0) + 1,
        ellipsisStartChar,
        ellipsisEndChar
      );
      spans.push(ellipsisSpan);
    },

    getExcerptSpans({ spans, limitFn, filterFn, charactersLeft }) {
      return spans.reduce((arr, span) => {
        if (limitFn(charactersLeft, span)) {
          const spanNodes = span.nodes.filter(filterFn);
          const newSpan = {
            ...span,
            nodes: spanNodes,
            fullText: spanNodes.map(n => n.nodeContent).join(''),
          };
          charactersLeft = charactersLeft - newSpan.fullText.length;
          return [...arr, newSpan];
        }

        return arr;
      }, []);
    },

    /**
     * Open all outline nodes down to the specified intentation level
     */
    showLevel(targetDepth) {
      let initialDepth = 1;
      this.outlineNodes
        .filter(node => node.children?.length)
        .forEach(n => {
          this.setOpenNode(n, initialDepth, targetDepth);
        });
    },

    /**
     * Expand all outline nodes
     */
    expandAll() {
      this.toggleAll(true);
    },

    /**
     * Collapse all outline nodes
     */
    collapseAll() {
      this.toggleAll(false, 1);
    },

    toggleAll(open, level) {
      EventBus.emit('outline.collapse.expand', true);
      this.showLevel(level);
      this.setSourceOutlineOpenStatus(open);
      this.initExpandedOutlineNodesIds();
      this.emitOutlineToggled();
    },

    /**
     * Recursively open outline node and it's children
     */
    setOpenNode(node, depth, targetDepth, isOpen) {
      if (Object.keys(this.nodeId).includes(node?._id) && Object.keys(this.nodeId).length) {
        node.open = this.nodeId[node?._id] === 'parent' ? false : this.nodeId[node?._id];
      } else {
        if (isOpen !== undefined) node.open = isOpen;
        else node.open = !targetDepth || depth < targetDepth;
      }
      if (node.children?.length)
        node.children.forEach(n => this.setOpenNode(n, depth + 1, targetDepth, isOpen));
    },

    setSourceOutlineOpenStatus(status) {
      this.flatOutlineSource
        .filter(node => node.children?.length)
        .forEach(node => {
          node.open = status;
        });
    },

    doMarkupClick(node) {
      if (
        (this.documentNodeAnnotations[node.nodeKey].counterpointAnnotationIDs &&
          this.documentNodeAnnotations[node.nodeKey].counterpointAnnotationIDs.length) ||
        (this.documentNodeLLMAnnotations[node.nodeKey].counterpointAnnotationIDs &&
          this.documentNodeLLMAnnotations[node.nodeKey].counterpointAnnotationIDs.length)
      )
        this.$emit('markupClick', node);
    },

    doMouseEnter(nodeOrSpan) {
      if (this.selectNode) {
        return false;
      }
      let node = nodeOrSpan.basisNodes ? nodeOrSpan.basisNodes[0] : nodeOrSpan;
      this.setSelectedOutlineNode(node);
      if (nodeOrSpan.basisNodes?.length) {
        if (this.myLatestRoundNumber === 1 && nodeOrSpan.basisNodes.length) {
          let newAnnotationPosition = {
            from: nodeOrSpan?.basisNodes[0],
            to: nodeOrSpan?.basisNodes.at(-1),
          };
          this.setNewAnnotationPosition(newAnnotationPosition);
        }
        this.$emit('textClick', {
          from: nodeOrSpan.basisNodes[0],
          to: nodeOrSpan.basisNodes.at(-1),
        });
      }
      if (nodeOrSpan.nodes?.length && !nodeOrSpan.basisNodes?.length) {
        if (this.myLatestRoundNumber === 1 && nodeOrSpan.nodes.length) {
          let newAnnotationPosition = { from: nodeOrSpan?.nodes[0], to: nodeOrSpan?.nodes.at(-1) };
          this.setNewAnnotationPosition(newAnnotationPosition);
        }
        this.$emit('textClick', { from: nodeOrSpan.nodes[0], to: nodeOrSpan.nodes.at(-1) });
      }
    },

    /**
     * Update geometries object with paragraph geometries data from the AnnotatedParagaph component
     */
    updateParagraphGeometries(geometries) {
      for (const key in geometries) {
        this.geometries[key] = geometries[key];
      }
      this.$emit('geometriesUpdated', this.geometries);
    },

    /**
     * Set container boundary for the arrow source element
     */
    setSourceElementContainerBoundary() {
      if (document.getElementById('outlineVirtualScrollContainer')) {
        let outlineContainerEl = document.getElementById('outlineVirtualScrollContainer');
        this.sourceElementContainerBoundary =
          getElementAbsolutePosition(outlineContainerEl).left +
          outlineContainerEl.offsetWidth -
          100;
      }
    },

    /**
     * Focus text editor inside outline node closest to click
     */
    outlineClick(ev) {
      if (!this.enableInput || this.canEditOutline) return;

      //
      // Calculate top positions for outline nodes.
      // Calculations per click are required in order to
      // account for any screen updates.
      let outlineNodesTops = {};
      forEachOutlineNode(this.outlineNodes, node => {
        const element = this.$refs[`outline-node-${node._id}`]?.$el;
        if (element)
          outlineNodesTops = {
            ...outlineNodesTops,
            [getElementAbsolutePosition(element).top]: node._id,
          };
      });
      //
      // determine closest node to click
      const outlineNodesTopsArray = Object.keys(outlineNodesTops);
      const closestNodeTopIndex = outlineNodesTopsArray.findIndex(x => x >= ev.pageY);
      const closestNodeTop = outlineNodesTopsArray[closestNodeTopIndex - 1];

      let nodeIdToFocus = outlineNodesTops[closestNodeTop];
      //
      // if unable to determine the node
      if (!nodeIdToFocus) {
        // if the event position is equal or less than the top node
        // use first node
        if (ev.pageY <= outlineNodesTopsArray[0])
          nodeIdToFocus = outlineNodesTops[outlineNodesTopsArray[0]];
        // otherwise, use the last node
        else
          nodeIdToFocus = outlineNodesTops[outlineNodesTopsArray[outlineNodesTopsArray.length - 1]];
      }

      //
      // focus on node
      this.$refs[`outline-node-input-${nodeIdToFocus}`].setFocus();
    },

    /**
     * Focus text editor inside outline node
     */
    focusNode(node) {
      if (this.enableInput) this.$refs[`outline-node-input-${node._id}`].setFocus();
    },

    /**
     * Functionality for tab keypress on an outline node for outline manipulation
     */
    doTab(e, node) {
      if (!this.enableInput) return;

      if (e.shiftKey) {
        this.outdentNode(node);
        setTimeout(() => {
          this.$refs[`outline-node-input-${node._id}`].setFocus();
        }, 100);
      } else {
        this.indentNode(node);
        setTimeout(() => {
          this.$refs[`outline-node-input-${node._id}`].setFocus();
        }, 100);
      }
    },

    getNodeIndex(node) {
      if (node) {
        const branch = node.parent.children;
        return branch.findIndex(x => x.nodeKey === node.nodeKey);
      }
    },

    /**
     * Functionality to outdent a node on press of the Tab + Alt keys on an outline node
     */
    async outdentNode(node) {
      if (node) {
        const branch = node.parent.children;
        const currentIndex = this.getNodeIndex(node);
        const parentToBe = node.parent.parent;

        // add to new parent
        let insertIndex = parentToBe.children.findIndex(
          n => parseInt(n.nodeOrder) > parseInt(node.nodeOrder)
        );
        insertIndex = insertIndex < 0 ? parentToBe.children.length : insertIndex;

        parentToBe.children.splice(insertIndex, 0, node);
        node.parent = parentToBe;

        // add siblings underneath as children
        const childrenToBe = branch.filter(n => parseInt(n.nodeOrder) > parseInt(node.nodeOrder));
        node.children = [...node.children, ...childrenToBe];

        // remove from old parent
        branch.splice(currentIndex, childrenToBe.length + 1);
        this.emitOutlineToggled();

        this.lastUpdatedNodeId = node._id;
        this.remapOutlineNodeOrder();
        if (this.selectNode) {
          return;
        }
        if (this.canEditOutline) {
          this.setBasisEdited(true);
        } else {
          await this.saveOutline();
        }
      }
    },

    /**
     * Functionality to indent a node on press of the Tab key on an outline node
     */
    async indentNode(node) {
      if (node) {
        let branch = node.parent.children;
        const currentIndex = this.getNodeIndex(node);
        const parentToBe = branch[currentIndex - 1];

        // Safety check: ensure parentToBe exists and has children array
        if (!parentToBe) return;
        if (!parentToBe.children) {
          parentToBe.children = [];
        }

        let insertIndex = parentToBe.children.length;

        parentToBe.children.splice(insertIndex, 0, node);
        node.parent = parentToBe;
        parentToBe.open = true;
        this.emitOutlineToggled();

        branch.splice(currentIndex, 1);

        this.lastUpdatedNodeId = node._id;
        this.remapOutlineNodeOrder();
        if (this.selectNode) {
          return;
        }

        if (this.canEditOutline) {
          this.setBasisEdited(true);
        } else {
          await this.saveOutline();
        }
      }
    },

    /**
     * Functionality for the special keys on an outline node for outline manipulation
     */
    doOutlineInputKeyDown({ ev, view, tiptap }, node) {
      this.lastUpdatedNodeId = null;
      this.lastUpdatedNodeOrder = null;
      switch (ev.code) {
        case 'Enter':
          ev.preventDefault(); // Prevent default Enter behavior
          this.doOutlineInputEnter(ev, view, tiptap, node);
          break;
        case 'ArrowUp':
          this.doOutlineInputArrowUp(ev, view, node);
          break;
        case 'ArrowDown':
          this.doOutlineInputArrowDown(ev, view, node);
          break;
        case 'ArrowLeft':
          this.doOutlineInputArrowUp(ev, view, node);
          break;
        case 'ArrowRight':
          this.doOutlineInputArrowDown(ev, view, node);
          break;
        case 'Backspace':
          this.doOutlineInputBackspace(ev, view, tiptap, node);
          break;
      }
      this.outlineNodeUpdated(node);
    },

    /**
     * Functionality for the Enter key on an outline node
     */
    async doOutlineInputEnter(ev, view, tiptap, node) {
      // Prevent default to stop newline being added to current input
      ev.preventDefault();

      let caretPosition = view.state.tr.curSelection.$head.pos;

      const nodeAfterEnter = view.state.tr.curSelection.$head.nodeAfter;

      let remainingTextLenght = nodeAfterEnter ? nodeAfterEnter.nodeSize : caretPosition;

      let endPosition = nodeAfterEnter ? caretPosition + remainingTextLenght + 1 : caretPosition;

      tiptap.editor
        .chain()
        .command(({ tr }) => {
          tr.delete(caretPosition, endPosition);
          return true;
        })
        .run();

      let nodeLevel = node.level;
      let nodeOrder = `${parseInt(node.nodeOrder) + 1}`;
      let nodeKey = `Node_${this.lastOutlineNodeKeyNumber++}`;

      let branch;
      let nodePosition;
      // find sub-array to splice
      // if node has children, add at the top
      if (node.children?.length) {
        branch = node.children;
        nodePosition = -1;
        node.open = true;
        this.emitOutlineToggled();
      } else {
        // get node parent
        branch = node.parent.children;
        nodePosition = branch.findIndex(n => n._id === node._id);
      }

      branch.splice(nodePosition + 1, 0, {
        level: nodeLevel,
        nodeContent: nodeAfterEnter ? nodeAfterEnter.textContent : '',
        nodeOrder,
        nodeKey,
        nodeType: 'HumanOutlineElement',
      });

      this.remapOutlineNodeOrder();

      this.lastUpdatedNodeOrder = nodeOrder;
      if (this.selectNode) {
        return;
      }
      this.isOutlineUpdated++;
    },

    /**
     * Functionality for the Backspace key on an outline node
     */
    async doOutlineInputBackspace(ev, view, tiptap, node) {
      const anchor = view.state.selection.$anchor;
      if (anchor.pos > 1) return;
      this.doDeleteNode(node);
    },

    doDeleteNode(node) {
      if (node) {
        let branch = node.parent.children;
        const currentIndex = this.getNodeIndex(node);
        const isFirstChild = currentIndex <= 0;

        // leave function if attempting to delete the topmost outline node
        if (isFirstChild && node.parent.isRoot) return;

        const nodeToMerge = this.getPreviousNode(node);
        const nodeToMergeContent = nodeToMerge.nodeContent.replace(/<[/]?p>/g, '');
        const nodeContent = node.nodeContent.replace(/<[/]?p>/g, '');

        nodeToMerge.nodeContent = `${nodeToMergeContent} ${nodeContent}`;

        const removedNode = branch.splice(currentIndex, 1)?.at(0);

        if (removedNode?.children?.length) {
          const nodeForChildren = isFirstChild
            ? // merging into parent
              removedNode.parent
            : // mergin into previous sibling
              branch[currentIndex - 1];

          if (nodeForChildren.children) {
            const mergeIndex = isFirstChild ? 0 : nodeForChildren.children.length;

            nodeForChildren.children.splice(mergeIndex, 0, ...removedNode.children);
          } else nodeForChildren.children = removedNode.children;
        }

        this.lastUpdatedNodeId = nodeToMerge._id;
        this.remapOutlineNodeOrder();
        if (this.selectNode) {
          return;
        }
        this.isOutlineUpdated++;
      }
    },

    /**
     * Remove the
     */
    doDeleteFirstOutineNode() {
      const node = this.outlineNodes[0];
      const nextNode = this.getNexNode(node);
      this.doDeleteNode(nextNode);
    },

    /**
     * Functionality for the Arrow Up key on an outline node
     */
    doOutlineInputArrowUp(ev, view, node) {
      const anchor = view.state.selection.$anchor;

      if (anchor.pos <= 1) {
        const prevNode = this.getPreviousNode(node);

        if (!prevNode?._id) {
          return;
        }

        this.lastUpdatedNodeId = prevNode._id;
        this.isOutlineUpdated++;
      }
    },

    /**
     * Functionality for the Arrow Down key on an outline node
     */
    doOutlineInputArrowDown(ev, view, node) {
      const docSize = view.state.doc.textContent.length;
      const anchor = view.state.selection.$anchor;

      if (anchor.pos > docSize) {
        const nextNode = this.getNexNode(node);

        if (!nextNode?._id) {
          return;
        }

        this.lastUpdatedNodeId = nextNode._id;
        this.isOutlineUpdated++;
      }
    },

    /**
     * Reassign the nodeKey, nodeOrder and level values of all nodes according to node global positions on the outline
     */
    remapOutlineNodeOrder() {
      this.nodeCount = 1;
      // remap node keys
      this.outlineNodes.forEach(n => this.remapNodeOrder(n, 1, this.nodeCount++));
    },

    /**
     * Reassign the nodeKey, nodeOrder and level values of a node and its children
     */
    remapNodeOrder(node, level, order) {
      node.nodeOrder = `${order}`;
      node.level = `${level}`;
      if (node.children)
        node.children.forEach(childNode => {
          this.remapNodeOrder(childNode, level + 1, this.nodeCount++);
        });
    },

    /**
     * retrieve previous node positioned immediately above the specified node using the global nodeOrder property
     */
    getPreviousNode(node) {
      return this.getFlatOutlineNodes().find(
        n => parseInt(n.nodeOrder) === parseInt(node.nodeOrder) - 1
      );
    },

    /**
     * retrieve next node positioned immediately below the specified node using the global nodeOrder property
     */
    getNexNode(node) {
      return this.getFlatOutlineNodes().find(
        n => parseInt(n.nodeOrder) === parseInt(node.nodeOrder) + 1
      );
    },

    /**
     * Returns a flat array version of all the outline nodes and their children
     */
    flattenOutline(outline) {
      return outline?.reduce(
        (acc, outlineNode) => [...acc, ...this.flattenOutlineChildren(outlineNode)],
        []
      );
    },

    /**
     * Returns an array with the outline node an its children
     * Recursive
     */
    flattenOutlineChildren(outlineNode) {
      const childrenArr = outlineNode.children || outlineNode.sublevel;
      if (childrenArr?.length) {
        let flatOutlineNodes = childrenArr.reduce(
          (acc, child) => {
            return [...acc, ...this.flattenOutlineChildren(child)];
          },
          [outlineNode]
        );
        return flatOutlineNodes;
      }
      return [outlineNode];
    },

    /**
     * Save outline on node focus out
     */
    doOutlineNodeFocusIn(node) {
      this.focusNodeId = node._id;
    },
    doOutlineNodeFocusOut(node) {
      if (this.selectNode) {
        return;
      }
      this.focusNodeId = node._id;
      if (this.canEditOutline) {
        this.setBasisEdited(true);
      } else {
        this.saveOutline();
      }
    },

    getDifference(array1, array2) {
      let first = [...array1];
      let second = [...array2];
      first.map(data => {
        let found = false;
        second.map((item, index) => {
          if (item.nodeContent === data.nodeContent && !found) {
            second.splice(index, 1);
            found = true;
          }
        });
      });
      return second;
    },

    upadteNodeAfterDrag(node) {
      if (node.parent.level) {
        node.level = node.parent.level + 1;
      } else {
        node.level = 1;
      }
      this.updateNodeLevel(node.level + 1, node.children);
      let nodeId = '';
      let isRoot = false;
      while (!isRoot) {
        if (!node.parent.isRoot) {
          nodeId = node.parent._id;
          node = node.parent;
        } else {
          isRoot = true;
        }
      }
      let updatedNodesData = [...this.outlineNodes];
      if (nodeId) {
        updatedNodesData = updatedNodesData.map(data => {
          if (data._id === nodeId) {
            data.sublevel = data.children;
          }
          return data;
        });
      }
      this.outlineNodes = updatedNodesData;
      this.setBasisEdited(true);
    },

    updateNodeLevel(level, children) {
      for (let el of children) {
        el.level = level;
        if (el.children) {
          this.updateNodeLevel(level + 1, el.children);
        }
      }
      return null;
    },

    handleDragEnd(node) {
      setTimeout(() => {
        this.isNodeDragged = true;
        this.upadteNodeAfterDrag(node);
      }, 1000);
    },

    /**
     * Clean-up outline empty nodes
     */
    cleanupOutlineEmptyNodes() {
      const flatOutline = this.getFlatOutlineNodes();
      const nodesToDelete = flatOutline.filter(
        node => node.nodeContent?.replace(/<[/]?p>/gi, '').trim() === ''
      );
      nodesToDelete.forEach(node => {
        const currentIndex = this.getNodeIndex(node);
        const isFirstChild = currentIndex <= 0;
        const isRootNode = node.parent.isRoot;

        if (isRootNode && isFirstChild) {
          this.doDeleteFirstOutineNode();
        } else this.doDeleteNode(node);
      });
    },

    /**
     * Save Outline
     */
    async saveOutline(success, failed) {
      this.cleanupOutlineEmptyNodes();

      try {
        if (this.outlineSource.outlineType === 'Human-Outline') return await this.saveNewOutline();
        else return await this.saveOutlineWithNodes(success, failed);
      } catch (err) {
        console.error(err);
        EventBus.emit('basis.save.error');
      }
    },
    // ***
    async saveNewOutline() {
      //
      if (this.roundStatus !== RoundStatus.EDITING_BASIS) {
        return false;
      }
      // if composing an outline from scratch (document source type is outline)
      const outlineToSave = {
        sourceType: 'Outline',
        content: null,
        analytical_content: null,
        publicationDate: null,
        sourceURL: null,
        nodes: null,
        outlineType: 'Human-Outline',
        outline: [...this.outlineNodes].map(node => getOutlineNodeForTransfer(node)),
      };
      try {
        const res = await this.updateSourceDocument({
          documentSourceData: outlineToSave,
        });
        this.setBasisEdited(false);
        this.$emit('updated', this.outlineNodes);
        if (this.documentData.initialStance === 'Host') {
          EventBus.emit('deabte.init.load');
        }
        return res;
      } catch (err) {
        EventBus.emit('mainActionButton.status', LoadingStatus.FAILED);
        console.error(err);
        return null;
      }
    },

    async saveOutlineWithNodes(success, failed) {
      //
      // validate that the outline content was updated
      const flatOutline = this.getFlatOutlineNodes();
      let outlineContentUpdated = false;

      let nodesData = {
        newNodes: [],
        oldNodes: [],
        deletedNodes: [],
        addedNodes: [],
        details: {},
      };
      let updatedNodeContent = '';
      //
      // if a node was created or removed to/from the outline root or any outline node
      if (flatOutline.length !== this.flatOutlineBeforeEdits.length) {
        outlineContentUpdated = true;
      } else {
        //
        // if an outline node was moved from one node to another
        // or if the content of a node changed (nodeContent)
        const childMoved = flatOutline.some((node, i) => {
          if (
            ((node.children?.length || 0) !==
              (this.flatOutlineBeforeEdits[i].sublevel?.length || 0) ||
              node.nodeContent !== this.flatOutlineBeforeEdits[i].nodeContent) &&
            this.canEditOutline
          ) {
            if (node.nodeContent !== this.flatOutlineBeforeEdits[i].nodeContent) {
              updatedNodeContent = node.nodeContent.replaceAll(/<[/]?p>/g, '');
              nodesData.oldNodes = this.flatOutlineBeforeEdits[i].nodes;
              nodesData.details = {
                paragraphId: this.flatOutlineBeforeEdits[i].paragraphId,
                id: this.flatOutlineBeforeEdits[i]._id,
              };
            }
          }
          return (
            (node.children?.length || 0) !==
              (this.flatOutlineBeforeEdits[i].sublevel?.length || 0) ||
            node.nodeContent !== this.flatOutlineBeforeEdits[i].nodeContent
          );
        });

        if (childMoved) outlineContentUpdated = true;
      }

      if (!outlineContentUpdated && !this.addOrDeleteNode) return this.outlineSource;

      //
      // clean nodes content before saving
      if (this.lastUpdatedNodeOrder) {
        //
        // remove any added paragraphs inside the previous node
        // (the lastUpdatedNodeOrder variable is set by pressing the Enter key on a TitTap text input, thus creating an extra <p>).
        const previousNode = this.getFlatOutlineNodes().find(
          n => parseInt(n.nodeOrder) === parseInt(this.lastUpdatedNodeOrder) - 1
        );
        if (previousNode?.nodeContent?.match('<p></p>'))
          previousNode.nodeContent = previousNode.nodeContent.replace(/<p><\/p>/g, '');
      }

      let outlineSource = null;
      let outlineNodes = null;
      // if(this.canEditOutline && updatedNodeContent && !this.isNodeDragged && !this.addOrDeleteNode) {
      //   nodesData.newNodes = (await this.getNodeFromText({text: updatedNodeContent}))?.thesis?.nodes || []
      //   nodesData.addedNodes = await this.getDifference(nodesData.oldNodes, nodesData.newNodes)
      //   nodesData.deletedNodes = await this.getDifference(nodesData.newNodes, nodesData.oldNodes)
      //   let mergedNode = false
      //   let updatedNodes = [...nodesData.oldNodes]
      //   while (!mergedNode) {
      //     let difference = this.patienceDiff(updatedNodes.map((data) => data.nodeContent), nodesData.newNodes.map((data) => data.nodeContent)).lines || []
      //     for (let i = 0; i < difference.length; i++) {
      //       if(difference[i].aIndex === -1) {
      //         let previousNode = nodesData.oldNodes[difference[i].bIndex - 1 <= 0 ? 0 : difference[i].bIndex - 1]
      //         if(!previousNode?.nodeKey) {
      //           previousNode = nodesData.oldNodes[difference[i].bIndex - 1 <= 0 ? 0 : difference[i].bIndex + 1]
      //         }
      //         let currentNode = nodesData.newNodes[difference[i].bIndex]
      //         let prevNodeKey = previousNode?.nodeKey.split(/T|[-]/)
      //         currentNode.nodeKey = `T${prevNodeKey ? prevNodeKey[2] : 0}-${parseInt(prevNodeKey ? prevNodeKey[2] : 0) + currentNode.nodeContent.length}`
      //         updatedNodes.splice(difference[i].bIndex, 0, currentNode);
      //         break;
      //       } else if(difference[i].bIndex === -1) {
      //         updatedNodes.splice(difference[i].aIndex, 1);
      //         break;
      //       } else if(difference.filter((data) => data.aIndex === -1 || data.bIndex === -1).length === 0) {
      //         mergedNode = true
      //         break;
      //       }
      //     }
      //   }
      //   let outlineNodeData = Array.from(this.outlineNodes).map((data) => {
      //     if(data.paragraphId === nodesData.details.paragraphId) {
      //       if(data._id === nodesData.details.id) {
      //         data.nodeContent = updatedNodes.map((node) => node.nodeContent).join("")
      //         data.nodes = updatedNodes
      //       } else {
      //         data.children = data.children.map((item) => {
      //           if(item._id === nodesData.details.id) {
      //             item.nodeContent = updatedNodes.map((node) => node.nodeContent).join("")
      //             item.nodes = updatedNodes
      //           }
      //           return item
      //         })
      //         if(data.sublevel) {
      //           data.sublevel = data.sublevel.map((item) => {
      //             if(item._id === nodesData.details.id) {
      //               item.nodeContent = updatedNodes.map((node) => node.nodeContent).join("")
      //               item.nodes = updatedNodes
      //             }
      //             return item
      //           })
      //         }
      //       }
      //     }
      //     return data
      //   })
      //   // ***
      //   let outlineSourceData = {...this.outlineSource}
      //   outlineSourceData.outline = Array.from(this.outlineSource.outlineType === "Human-Outline" ? this.outlineSource.outline : this.outlineSource.outline.outline).map((data) => {
      //     if(data.paragraphId === nodesData.details.paragraphId) {
      //       if(data._id === nodesData.details.id) {
      //         data.nodeContent = updatedNodes.map((node) => node.textFragmentContent).join("")
      //         data.nodes = updatedNodes
      //       } else {
      //         if(data.children) {
      //           data.children = data.children.map((item) => {
      //             if(item._id === nodesData.details.id) {
      //               item.nodeContent = updatedNodes.map((node) => node.textFragmentContent).join("")
      //               item.nodes = updatedNodes
      //             }
      //             return item
      //           })
      //         }
      //         if(data.sublevel) {
      //           data.sublevel = data.sublevel.map((item) => {
      //             if(item._id === nodesData.details.id) {
      //               item.nodeContent = updatedNodes.map((node) => node.textFragmentContent).join("")
      //               item.nodes = updatedNodes
      //             }
      //             return item
      //           })
      //         }
      //       }
      //     }
      //     return data
      //   });

      //   outlineSource = outlineSourceData;
      //   outlineNodes = outlineNodeData;

      // } else

      if (this.addOrDeleteNode || this.isNodeDragged || updatedNodeContent) {
        let outlineSourceData = { ...this.outlineSource };
        outlineSourceData.outline = this.outlineNodes;

        outlineSource = outlineSourceData;
        outlineNodes = this.outlineNodes;
      } else {
        outlineSource = this.outlineSource;
        outlineNodes = this.outlineNodes;
      }
      let res = null;
      await this.updateOutline({
        outlineSource,
        outlineNodes,
        successFn: result => {
          res = result;
          this.getDocumentWithCounterpoints(() => {
            void 0;
          });
          this.updateFlatOutlineBeforeEdits(
            result.outline.map(node => getOutlineNodeForDisplay(node))
          );
          if (success) success();
        },
        failedFn: error => {
          if (failed) failed();
          this.showToastAlert({
            title: 'Error updating outline',
            message: `${error}<br />Please, refresh your browser`,
          });
        },
      });
      //
      // save node content
      //
      // update outlineNodes on this component
      this.isOutlineUpdated++;
      this.setBasisEdited(false);
      return res;
    },

    outlineNodeHoverIn(outlineNode, ev) {
      this.hoverNodeId = outlineNode._id;
      this.setupOutlineParagraphs();
      EventBus.emit('outline.node.hoverIn', outlineNode, ev);
      this.outlineNodeUpdated(outlineNode);
    },

    outlineNodeHoverOut(outlineNode, ev) {
      if (this.activeAnnotation) {
        return;
      }
      this.hoverNodeId = '';
      this.setupOutlineParagraphs();
      if (this.useExcerpts) {
        EventBus.emit('outline.node.hoverOut', outlineNode, ev);
      }
      this.outlineNodeUpdated(outlineNode);
    },
    // ***
    initExpandedOutlineNodesIds() {
      const allNodeIds = this.nodeId;
      let outlineData = this.outlineSource?.outline?.outline
        ? this.outlineSource.outline.outline
        : this.outlineSource.outline;
      for (const outlineNode of outlineData.filter(node => node.children?.length)) {
        this.setExpandedOutlineNodesIds(outlineNode, outlineNode.children, allNodeIds);
      }
      if (Object.keys(allNodeIds).length) {
        this.setNodeId({ ...allNodeIds });
      }
    },

    toggleOutlineNodeClicked(data, store) {
      let childNodes = data.children;
      if (!childNodes?.length) return;

      store.toggleOpen(data);

      let newNodeIds = {};
      newNodeIds[data._id] = data.open ? true : 'parent';
      this.setExpandedOutlineNodesIds(data, childNodes, newNodeIds);
      if (newNodeIds[data._id] !== false) {
        this.setNodeId({ ...this.nodeId, ...newNodeIds });
      }
      //
      // toggle values on the original source outline
      const outlineNode = this.flatOutlineSource.find(node => node._id === data._id);
      this.setOpenNode(outlineNode, 1, null, data.open);
      setTimeout(() => {
        EventBus.emit('UI.position.annotations', 0, 'temp');
      }, 800);
      this.emitOutlineToggled();
    },

    emitOutlineToggled() {
      //
      // emit the proportion of open nodes
      const flatOutlineNodesWithChildren = this.getFlatOutlineNodes().filter(
        node => node.children?.length
      );
      const openNodesRatio =
        flatOutlineNodesWithChildren.filter(node => node.open).length /
        flatOutlineNodesWithChildren.length;
      EventBus.emit('outline.toggled', openNodesRatio);
    },

    setExpandedOutlineNodesIds(data, outlineNodes, newNodeIds) {
      if (!outlineNodes) return;
      for (const node of outlineNodes) {
        newNodeIds[node._id] = data.open;
        if (node.children?.length) {
          this.setExpandedOutlineNodesIds(data, node.children, newNodeIds);
        }
      }
    },

    getOutlineNodeWrapperElementHeight(data) {
      const nodeElement = document.querySelector(`[data-node-id="${data._id}"]`);
      const nodeWrapperElement = nodeElement?.closest('.tree-node');

      return nodeWrapperElement ? nodeWrapperElement.offsetHeight : 0;
    },

    rootNodeLineHeight(data) {
      const isLastChild =
        this.outlineNodes.length === this.outlineNodes.findIndex(n => n._id === data._id) + 1;
      let height = 0;
      if (isLastChild) {
        height = '1px';
      } else if (this.useExcerpts) {
        height = `${10 * (data.totalRowHeight / 2)}rem`;
      } else if (data.children?.length) {
        const nodeWrapperElementHeight = this.getOutlineNodeWrapperElementHeight(data);
        if (nodeWrapperElementHeight) {
          height = `${nodeWrapperElementHeight + 8}px`;
        }
      } else {
        height = 'calc(100% + 8px)';
      }
      data.nodeHeight = height;
    },

    /**
     * Assign the height for the dotted vertical line of a parent node (containing child nodes)
     */
    parentNodeLineHeight(data) {
      let parentNodeLineHeight = '';
      if (this.useExcerpts) {
        const lastChild = data.children[data.children.length - 1];
        const lastChildTotalRowHeight = lastChild.totalRowHeight;
        const totalHeightGap = 10 * lastChildTotalRowHeight + 0.35;

        const totalHeight = 10 * data.totalRowHeight - totalHeightGap;
        parentNodeLineHeight = `${totalHeight / 2}rem`;
      } else if (data.children?.length) {
        //
        const nodeWrapperElementHeight = this.getOutlineNodeWrapperElementHeight(data);
        if (nodeWrapperElementHeight) {
          const lastChild = data.children[data.children.length - 1];
          const lastChildNodeWrapperElementHeight =
            this.getOutlineNodeWrapperElementHeight(lastChild);

          parentNodeLineHeight = `${nodeWrapperElementHeight - lastChildNodeWrapperElementHeight - 8}px`;
        }
      }
      //
      // default height
      else {
        parentNodeLineHeight = 'calc(100% + .15rem)';
      }
      //
      // setting node line height for parent node
      data.parentNodeLineHeight = parentNodeLineHeight;
    },

    /**
     * Outline node was updated
     */
    outlineNodeUpdated(data) {
      if (data.level <= 1) this.rootNodeLineHeight(data);
      if (data.open && data.children?.length) this.parentNodeLineHeight(data);
      if (data.parent && !data.parent.isRoot) this.outlineNodeUpdated(data.parent);
    },

    setupOutline() {
      this.setupOutlineNodes(this.outlineSource);
      this.setupOutlineParagraphs();
    },
  },
};
</script>

<style lang="sass" scoped>
.undo-passage
  display: none
  left: 17%
  top: 85%
  position: fixed
  background: white
  border-radius: 15px
  box-shadow: rgb(0 0 0 / 35%) 0px 5px 15px
  padding: 12px
  height: 11%

.select-node
  display: flex
  left: 20%
  top: 90%
  position: fixed
  background: white
  border-radius: 15px
  box-shadow: rgb(0 0 0 / 35%) 0px 5px 15px
  padding: 6px
  z-index: 999
.select-node-left
  left: 70% !important
.text-selected
  background: rgba(200, 221, 233, .5)
  border-radius: 15px

.arrowOpen
  img
    transform: rotate(90deg)
.arrowClose
  img
    transform: rotate(0deg)

.outline-ban-icon
  right: .8rem !important
</style>
